import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {Calendar, Heart, Briefcase, Activity, Sparkles} from 'lucide-react';
import { lumi } from "../lib/lumi";
import toast from "react-hot-toast";

const zodiacSigns = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"
];

export function DailyHoroscope() {
  const [selectedSign, setSelectedSign] = useState<string>("");
  const [horoscope, setHoroscope] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    // Check if user has a saved Rising sign from calculator
    const savedBig3 = localStorage.getItem("userBig3");
    if (savedBig3) {
      const big3 = JSON.parse(savedBig3);
      setSelectedSign(big3.rising);
      fetchHoroscope(big3.rising, selectedDate);
    }
  }, []);

  const fetchHoroscope = async (sign: string, date: string) => {
    setLoading(true);
    try {
      const response = await lumi.functions.invoke("getDailyHoroscope", {
        method: "POST",
        body: { sign, date },
      });

      if (response.success) {
        setHoroscope(response.data);
      } else {
        toast.error("Failed to fetch horoscope. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching horoscope:", error);
      toast.error("Unable to load horoscope. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignSelect = (sign: string) => {
    setSelectedSign(sign);
    fetchHoroscope(sign, selectedDate);
  };

  return (
    <div className="min-h-screen py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">

        {/* Affiliate Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <a
            href="https://hop.clickbank.net/?affiliate=ascendcalc&vendor=thoughtop"
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="block w-full rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#0B0E14] via-[#1a1608] to-[#0B0E14] hover:border-[#D4AF37]/80 hover:from-[#131108] hover:via-[#2a2210] hover:to-[#131108] transition-all duration-300 shadow-lg shadow-[#D4AF37]/5 group"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✨</span>
                <div className="text-center sm:text-left">
                  <p className="text-[#D4AF37] font-semibold text-sm uppercase tracking-widest">Sponsored</p>
                  <p className="text-white font-serif text-base md:text-lg">
                    Unlock Your Full Cosmic Potential — Discover Your Destiny Today
                  </p>
                </div>
              </div>
              <span className="shrink-0 px-5 py-2 rounded-full bg-[#D4AF37] text-[#0B0E14] text-sm font-bold group-hover:bg-[#FCD34D] transition-colors duration-300">
                Explore Now →
              </span>
            </div>
          </a>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Daily <span className="text-[#D4AF37]">Horoscope</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Discover what the cosmos has in store for your Rising Sign today. 
            Personalized guidance for love, career, health, and more.
          </p>
        </motion.div>

        {/* Date Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-[#131722] border border-white/10 rounded-lg px-4 py-2 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#D4AF37]" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                if (selectedSign) {
                  fetchHoroscope(selectedSign, e.target.value);
                }
              }}
              max={new Date().toISOString().split("T")[0]}
              className="bg-transparent text-white outline-none"
            />
          </div>
        </div>

        {/* Zodiac Sign Selector */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-12">
          {zodiacSigns.map((sign) => (
            <button
              key={sign}
              onClick={() => handleSignSelect(sign)}
              className={`p-4 rounded-xl border transition-all ${
                selectedSign === sign
                  ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                  : "bg-[#131722] border-white/10 text-gray-400 hover:border-[#D4AF37]/50"
              }`}
            >
              <div className="text-2xl mb-1">{getZodiacEmoji(sign)}</div>
              <div className="text-xs font-medium">{sign}</div>
            </button>
          ))}
        </div>

        {/* Horoscope Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : horoscope ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Overview Card */}
            <div className="bg-[#131722] border border-white/10 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                <h2 className="text-2xl font-serif font-bold text-white">Today's Overview</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed">{horoscope.overview}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                <InfoPill label="Lucky Color" value={horoscope.luckyColor} />
                <InfoPill label="Lucky Number" value={horoscope.luckyNumber} />
                <InfoPill label="Compatibility" value={horoscope.compatibility} />
              </div>
            </div>

            {/* Detailed Predictions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PredictionCard
                icon={<Heart className="w-6 h-6" />}
                title="Love & Relationships"
                content={horoscope.love}
                color="text-pink-400"
              />
              <PredictionCard
                icon={<Briefcase className="w-6 h-6" />}
                title="Career & Finance"
                content={horoscope.career}
                color="text-blue-400"
              />
              <PredictionCard
                icon={<Activity className="w-6 h-6" />}
                title="Health & Wellness"
                content={horoscope.health}
                color="text-green-400"
              />
              <div className="bg-[#131722] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-serif font-bold text-white mb-3">
                  Need Your Full Birth Chart?
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Calculate your exact Rising Sign and discover your complete astrological profile.
                </p>
                <a
                  href="/"
                  className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0B0E14] font-medium rounded-lg hover:bg-[#FCD34D] transition-colors"
                >
                  Calculate Now
                </a>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">Select your Rising Sign above to view your daily horoscope</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PredictionCard({ icon, title, content, color }: { icon: React.ReactNode; title: string; content: string; color: string }) {
  return (
    <div className="bg-[#131722] border border-white/10 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={color}>{icon}</div>
        <h3 className="text-lg font-serif font-bold text-white">{title}</h3>
      </div>
      <p className="text-gray-300 leading-relaxed">{content}</p>
    </div>
  );
}

function InfoPill({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-[#0B0E14] rounded-lg p-3 border border-white/5">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-[#D4AF37] font-semibold">{value}</div>
    </div>
  );
}

function getZodiacEmoji(sign: string): string {
  const emojis: { [key: string]: string } = {
    Aries: "♈",
    Taurus: "♉",
    Gemini: "♊",
    Cancer: "♋",
    Leo: "♌",
    Virgo: "♍",
    Libra: "♎",
    Scorpio: "♏",
    Sagittarius: "♐",
    Capricorn: "♑",
    Aquarius: "♒",
    Pisces: "♓",
  };
  return emojis[sign] || "⭐";
}
